/* eslint-disable react/no-array-index-key */
import useApi from '@propertypal/shared/src/hooks/useApi';
import OgImage from '@propertypal/shared/src/resources/images/open-viewings/open-graph-open-viewings.jpg';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import dayjs from '@propertypal/shared/src/services/dayjs';
import { ViewingSearchResult } from '@propertypal/shared/src/types/viewing';
import platform from '@propertypal/shared/src/utils/platform';
import { Box, RowCenter } from '@propertypal/web-ui/src/layout';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import { MAX_WIDTH } from '@propertypal/web-ui/src/layout/PageWrapper.style';
import GridContainer from '@propertypal/web-ui/src/property/PropertyGrid.style';
import PropertyPlaceholder from '@propertypal/web-ui/src/property/PropertyPlaceholder';
import PropertyBox from '@propertypal/web-ui/src/property-box/PropertyBox';
import Tabs from '@propertypal/web-ui/src/tabs/Tabs';
import { Heading, Text } from '@propertypal/web-ui/src/typography';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next/types';
import React, { FC, useState } from 'react';
import generateMetaTags from '../components/layout/MetaTags';
import IMAGE_LOCATOR, { IMAGE_LOCATOR_LARGE } from '../constants/imageLocator';
import usePropertyFavourite from '../hooks/usePropertyFavourite';
import { requireAuthToken } from '../utils/auth';

const metaData = {
  // eslint-disable-next-line max-len
  description: `Theres no better way to get a feel for a house than seeing it. With ${platform.title} Open Viewings you'll be able to see if a home is a perfect fit in person.`,
  url: `/open-viewings`,
};

interface Props {
  loggedIn: boolean;
  initialDate: string;
  data: ViewingSearchResult;
}

export const getServerSideProps: GetServerSideProps = requireAuthToken(async (ctx) => {
  let query = '';

  if (typeof ctx.query.saleType === 'string') {
    query += `&saleType=${ctx.query.saleType}`;
  }

  if (typeof ctx.query.term === 'string') {
    query += `&term=${encodeURIComponent(ctx.query.term)}`;
  }

  query += `&date=${ctx.query.date || dayjs().format('YYYY-MM-DD')}`;

  const result = await axios(
    {
      url: `${API_URL}/open-viewings?imageLocator=${encodeURIComponent(IMAGE_LOCATOR)}${query}`,
    },
    undefined,
  );

  return {
    props: {
      initialDate: ctx.query.date || dayjs().format('YYYY-MM-DD'),
      data: result.data,
    },
  };
});

const getTown = (viewings?: ViewingSearchResult) => {
  if (viewings?.locationDetail) {
    return ` in ${viewings.locationDetail.name}`;
  }

  return '';
};

const getTabs = (openViewingDates?: string[]) => {
  const today = dayjs();
  const tabs = [];

  for (let i = 0; i < 7; i += 1) {
    const weekday = today.add(i, 'day');
    const dateStr = weekday.format('YYYY-MM-DD');
    const disabled = openViewingDates && openViewingDates.findIndex((d) => d.includes(dateStr)) < 0;

    tabs.push({
      key: dateStr,
      prefix: i === 0 ? 'Today' : weekday.format('ddd'),
      text: weekday.format('DD MMM'),
      disabled,
    });
  }

  return tabs;
};

const OpenViewings: FC<Props> = (props) => {
  const [data, , loading, fetchData] = useApi(`/open-viewings`, false, props.data);
  const town = getTown(data);
  const title = `Open Viewings ${town}${
    data?.params.saleType && data?.params.saleType[0] === 'sale' ? ' (To Rent)' : ' (For Sale)'
  } - ${platform.title}`;
  const onFavourite = usePropertyFavourite(props.loggedIn);
  const [tabs] = useState(getTabs(data?.openViewingDates));
  const [tab, setTab] = useState(props.initialDate);
  const router = useRouter();

  const updateDate = (date: string) => {
    let query = `imageLocator=${encodeURIComponent(IMAGE_LOCATOR)}&date=${date}`;
    if (typeof router.query.term === 'string') query += `&term=${encodeURIComponent(router.query.term)}`;
    if (router.query.saleType) query += `&saleType=${router.query.saleType}`;

    setTab(date);
    fetchData(`/open-viewings?${query}`);
  };

  return (
    <>
      <Head>
        {generateMetaTags({
          title,
          description: metaData.description,
          url: metaData.url,
          image: {
            url: OgImage.url,
            width: 1200,
            height: 650,
          },
        })}
      </Head>

      <PageWrapper containerStyle={{ padding: '30px 0' }}>
        <Heading data-testid="open-viewings-heading">Open Viewings {town}</Heading>
      </PageWrapper>

      {tabs && <Tabs tabs={tabs} activeTab={tab} onTabClick={updateDate} />}

      {loading && (
        <RowCenter data-testid="loading-viewings">
          <Box maxWidth={MAX_WIDTH} m="0 auto">
            <GridContainer role="listbox" maxColumns={4}>
              {Array.from({ length: 4 }).map((_, i) => (
                <PropertyPlaceholder key={i} />
              ))}
            </GridContainer>
          </Box>
        </RowCenter>
      )}

      {!loading && data?.results.length === 0 && (
        <Box ta="center" m="50px 0px" data-testid="empty-viewings" p="0px 12px">
          <Heading>No Viewings Scheduled</Heading>
          <Text mt={5} data-testid="empty-text">
            There are no open viewings {`scheduled for ${dayjs(tab, 'YYYY-MM-DD').format('dddd MMMM DD YYYY')}`}.
          </Text>
        </Box>
      )}

      {!loading && !!data?.results.length && (
        <RowCenter data-testid="viewing-results">
          <Box maxWidth={MAX_WIDTH} m="0 auto">
            <GridContainer role="listbox" maxColumns={4}>
              {data.results.map((a) => (
                <PropertyBox
                  key={a.id}
                  property={a}
                  showOpenViewingLabel
                  onFavourite={onFavourite}
                  imageLocator={IMAGE_LOCATOR_LARGE}
                  disablePremiumProperties
                />
              ))}
            </GridContainer>
          </Box>
        </RowCenter>
      )}
    </>
  );
};

export default OpenViewings;
